/**
 * Fichier de mapping des IDs de la base de données vers les valeurs utilisables
 * Les mappings de tailles sont chargés dynamiquement depuis l'API
 */

import { API_MOBILE_CONFIG } from './config';
import logger from '../services/logger';

/**
 * Mapping des IDs de genre vers les valeurs textuelles
 * @type {Object.<number, string>}
 */
export const GENDER_MAPPING = {
  1: 'homme',
  2: 'femme',
};

/**
 * Mapping inverse: des valeurs textuelles vers les IDs de genre
 * @type {Object.<string, number>}
 */
export const REVERSE_GENDER_MAPPING = {
  'homme': 1,
  'femme': 2,
};

// Fallback en dur au cas où l'API est indisponible
const FALLBACK_SIZE_MAPPING = {
  1: 'XXS',
  2: 'XS',
  3: 'S',
  4: 'M',
  5: 'L',
  6: 'XL',
  7: 'XXL',
  8: '3XL',
  9: '4XL',
  10: '5XL',
  11: '6XL',
};

const FALLBACK_REVERSE_SIZE_MAPPING = {
  'XXS': 1,
  'XS': 2,
  'S': 3,
  'M': 4,
  'L': 5,
  'XL': 6,
  'XXL': 7,
  '3XL': 8,
  '4XL': 9,
  '5XL': 10,
  '6XL': 11,
};

/**
 * Mappings dynamiques chargés depuis l'API
 * Initialisés avec les fallbacks, remplacés au chargement
 */
export let PANTS_SIZE_MAPPING = { ...FALLBACK_SIZE_MAPPING };
export let TOP_SIZE_MAPPING = { ...FALLBACK_SIZE_MAPPING };
export let REVERSE_PANTS_SIZE_MAPPING = { ...FALLBACK_REVERSE_SIZE_MAPPING };
export let REVERSE_TOP_SIZE_MAPPING = { ...FALLBACK_REVERSE_SIZE_MAPPING };

// IDs correspondant à une taille unique (fallback = ID 34, remplacé dynamiquement au chargement)
export let ONE_SIZE_IDS = new Set([34]);

// Regex pour détecter les libellés de taille unique dans la réponse API
const ONE_SIZE_LABEL_RE = /^(tu|taille[ -]?unique|one[ -]?size|os|unique)$/i;

let _sizeMappingsLoaded = false;

/**
 * Récupère toutes les tailles depuis l'API (gère la pagination)
 * @param {number} sizeGroup - ID du groupe de tailles à récupérer
 * @returns {Promise<Array>} - Liste de toutes les tailles {id, fr_display, size_group}
 */
const fetchAllSizes = async (sizeGroup = 4) => {
  const allResults = [];
  let url = `${API_MOBILE_CONFIG.BASE_URL}/api/sizes?size_group=${sizeGroup}`;

  while (url) {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    const data = await response.json();
    const results = data.results || data;

    if (Array.isArray(results)) {
      allResults.push(...results);
    }

    // Page suivante si paginé
    url = data.next || null;
  }

  return allResults;
};

/**
 * Charge les mappings de tailles depuis l'API
 * Construit les mappings id→fr_display et fr_display→id
 * @returns {Promise<boolean>} - true si le chargement a réussi
 */
export const fetchSizeMappings = async () => {
  if (_sizeMappingsLoaded) {
    return true;
  }

  try {
    if (!API_MOBILE_CONFIG.BASE_URL) {
      logger.warn('⚠️ API_MOBILE_CONFIG.BASE_URL non configuré, utilisation des mappings par défaut');
      return false;
    }

    const sizes = await fetchAllSizes(4);

    if (!sizes || sizes.length === 0) {
      logger.warn('⚠️ Aucune taille récupérée depuis l\'API, utilisation des mappings par défaut');
      return false;
    }

    // Construire les mappings id → fr_display
    const sizeMapping = {};
    const reverseSizeMapping = {};
    const oneSizeIds = new Set();

    for (const size of sizes) {
      if (size.id && size.fr_display) {
        sizeMapping[size.id] = size.fr_display;
        reverseSizeMapping[size.fr_display] = size.id;

        // Détecter les tailles uniques par leur libellé (size_label ou fr_display)
        if (ONE_SIZE_LABEL_RE.test(size.size_label) || ONE_SIZE_LABEL_RE.test(size.fr_display)) {
          oneSizeIds.add(size.id);
        }
      }
    }

    // Mettre à jour les mappings (même table pour haut et bas)
    PANTS_SIZE_MAPPING = sizeMapping;
    TOP_SIZE_MAPPING = sizeMapping;
    REVERSE_PANTS_SIZE_MAPPING = reverseSizeMapping;
    REVERSE_TOP_SIZE_MAPPING = reverseSizeMapping;
    ONE_SIZE_IDS = oneSizeIds.size > 0 ? oneSizeIds : new Set([34]);

    _sizeMappingsLoaded = true;
    logger.log('✅ Mappings de tailles chargés depuis l\'API:', {
      count: sizes.length,
      sizes: Object.values(sizeMapping),
      oneSizeIds: [...ONE_SIZE_IDS],
    });

    return true;
  } catch (error) {
    logger.error('❌ Erreur lors du chargement des mappings de tailles:', error);
    logger.warn('⚠️ Utilisation des mappings par défaut');
    return false;
  }
};

/**
 * Convertit les données brutes de la DB vers le format utilisé dans l'application
 * @param {Object} shopperData - Données brutes du shopper depuis la DB
 * @returns {Object} - Données formatées pour l'application
 */
export const mapShopperDataFromDB = (shopperData) => {
  if (!shopperData) {
    return null;
  }

  const {
    gender_id,
    size_cm,
    weight_kg,
    regular_pants_size_id,
    regular_top_size_id,
    ...otherFields
  } = shopperData;

  // Mapping des IDs vers les valeurs
  const gender = GENDER_MAPPING[gender_id] || '';
  const sizeBottom = PANTS_SIZE_MAPPING[regular_pants_size_id] || '';
  const sizeTop = TOP_SIZE_MAPPING[regular_top_size_id] || '';

  // Validation: log un warning si des mappings sont manquants
  if (gender_id && !gender) {
    logger.warn(`⚠️ Gender ID ${gender_id} non trouvé dans GENDER_MAPPING`);
  }
  if (regular_pants_size_id && !sizeBottom) {
    logger.warn(`⚠️ Pants size ID ${regular_pants_size_id} non trouvé dans PANTS_SIZE_MAPPING`);
  }
  if (regular_top_size_id && !sizeTop) {
    logger.warn(`⚠️ Top size ID ${regular_top_size_id} non trouvé dans TOP_SIZE_MAPPING`);
  }

  return {
    ...otherFields,
    // Champs utilisés par l'application
    gender: gender,
    height: size_cm || null,
    weight: weight_kg || null,
    sizeBottom: sizeBottom,
    sizeTop: sizeTop,
    // Garder aussi les IDs originaux pour référence
    _ids: {
      gender_id,
      regular_pants_size_id,
      regular_top_size_id,
    },
  };
};

/**
 * Convertit les données de l'application vers le format de la DB
 * Utilisé lors de la création/mise à jour d'un shopper
 * @param {Object} userData - Données formatées de l'application
 * @returns {Object} - Données formatées pour la DB
 */
export const mapUserDataToDB = (userData) => {
  if (!userData) {
    return null;
  }

  const {
    gender,
    height,
    weight,
    sizeBottom,
    sizeTop,
    ...otherFields
  } = userData;

  // Mapping des valeurs vers les IDs
  const gender_id = REVERSE_GENDER_MAPPING[gender] || null;
  const regular_pants_size_id = REVERSE_PANTS_SIZE_MAPPING[sizeBottom] || null;
  const regular_top_size_id = REVERSE_TOP_SIZE_MAPPING[sizeTop] || null;

  // Validation: log un warning si des mappings sont manquants
  if (gender && !gender_id) {
    logger.warn(`⚠️ Genre "${gender}" non trouvé dans REVERSE_GENDER_MAPPING`);
  }
  if (sizeBottom && !regular_pants_size_id) {
    logger.warn(`⚠️ Taille pantalon "${sizeBottom}" non trouvée dans REVERSE_PANTS_SIZE_MAPPING`);
  }
  if (sizeTop && !regular_top_size_id) {
    logger.warn(`⚠️ Taille haut "${sizeTop}" non trouvée dans REVERSE_TOP_SIZE_MAPPING`);
  }

  return {
    ...otherFields,
    // Champs pour la DB
    gender_id: gender_id,
    size_cm: height || null,
    weight_kg: weight || null,
    regular_pants_size_id: regular_pants_size_id,
    regular_top_size_id: regular_top_size_id,
  };
};
